/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DeviceConfigurationService,
  TsriServiceEndpointService
} from "@tivocorp/data-domain-lib";
import type {
  DeviceBrandingConfig,
  ServiceEndpoint,
  ServiceEndpoints,
  StringOverrides,
  TTsriServiceEndpointSearchParams,
  TServicesParams
} from "@tivocorp/data-domain-lib";

import type { IAppStartupInteractor } from "../../interfaces/IAppStartupInteractor";
import { Log } from "../../logging/Log";
import { DiagnosticLogger } from "../../logging/DiagnosticLogger";
import { DiagnosticSeverity } from "../../types";
import { Locale, LocaleKeys } from "../../locale";
import { CommonHeaders } from "../../CommonHeaders";
import { ApplicationFeatureArea } from "../../enums";
import {
  EmptyResponseError,
  ErrorTypes,
  MsoDefaultDeviceConfigError,
  MsoDefaultStringOverridesError,
  PartialResponseError,
  TsriServiceEndpointError
} from "../../error";
import { attachHttpContext } from "../../error/attachHttpContext";
import {
  REQUIRED_PRE_SIGN_IN_SERVICES,
  TSRI_BASE_URL,
  TIVO_SERVICES
} from "../../../config";
import { transformBaseUrl } from "../../../devtools/mockOverride";
import { DeviceModel } from "../../device";
import { TsriEndpointResolver } from "../../tsri";
import { DimensionScaler } from "../../utility/DimensionScaler";
import { Branding } from "../../branding";

/**
 * Facade class that wraps TsriServiceEndpointService behind a single
 * entry point for fetching service endpoints during application
 * startup.
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 */
export class AppStartupInteractor implements IAppStartupInteractor {
  private static TAG: string = "AppStartupInteractor";

  /**
   * Builds the TServicesParams used to instantiate domain-lib services.
   * Common metadata is derived from DeviceModel via CommonHeaders.
   */
  private static buildServicesParams(): TServicesParams {
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: transformBaseUrl(TSRI_BASE_URL)
    });
  }

  private isTsriResIncomplete = (tsriEndpoints: ServiceEndpoints): boolean => {
    const endpoints: ServiceEndpoint[] =
      tsriEndpoints.serviceEndpoints !== undefined &&
      tsriEndpoints.serviceEndpoints !== null
        ? tsriEndpoints.serviceEndpoints
        : [];
    const services: string[] = endpoints.map(
      (se: ServiceEndpoint) => se.service
    );
    for (let i = 0; i < REQUIRED_PRE_SIGN_IN_SERVICES.length; i += 1) {
      if (!services.includes(REQUIRED_PRE_SIGN_IN_SERVICES[i])) {
        return true;
      }
    }
    return false;
  };

  private validateTsriResponse = (tsriEndpoints: ServiceEndpoints) => {
    const endpoints: ServiceEndpoint[] | undefined =
      tsriEndpoints.serviceEndpoints;
    if (
      endpoints === undefined ||
      endpoints === null ||
      endpoints.length === 0
    ) {
      throw new EmptyResponseError(
        ErrorTypes.TSRI_EMPTY_RESPONSE_A012,
        "Received empty response from TsriServiceEndpointGet",
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (this.isTsriResIncomplete(tsriEndpoints)) {
      throw new PartialResponseError(
        ErrorTypes.TSRI_PARTIAL_RESPONSE_A015,
        "Received partial response from TsriServiceEndpointGet",
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  };

  public async fetchGlobalEndpoints(
    params: TTsriServiceEndpointSearchParams,
    signal?: AbortSignal
  ): Promise<ServiceEndpoints> {
    try {
      Log.d(AppStartupInteractor.TAG, "fetchGlobalEndpoints() called");
      const startTime: number = Date.now();
      const service: TsriServiceEndpointService =
        new TsriServiceEndpointService(
          AppStartupInteractor.buildServicesParams()
        );
      const response = await service.fetchServiceEndpoints(
        {
          tsriId: params.tsriId,
          originRequestId: params.originRequestId
        },
        signal
      );
      this.validateTsriResponse(response);
      DiagnosticLogger.log(
        "pre_signin_api",
        "TsriServiceEndpointService succeeded",
        DiagnosticSeverity.Info,
        {
          api: "TsriServiceEndpointService",
          status: "success",
          latency_ms: String(Date.now() - startTime)
        }
      );
      return response;
    } catch (error: unknown) {
      DiagnosticLogger.log(
        "pre_signin_api",
        "TsriServiceEndpointService failed",
        DiagnosticSeverity.Error,
        {
          api: "TsriServiceEndpointService",
          status: "failure",
          error_type:
            error instanceof Error ? error.constructor.name : "unknown"
        }
      );
      if (
        error instanceof EmptyResponseError ||
        error instanceof PartialResponseError
      ) {
        Log.e(
          AppStartupInteractor.TAG,
          "fetchGlobalEndpoints() validation failed",
          error
        );
        throw error;
      }
      const tsriError: unknown =
        error !== null &&
        typeof error === "object" &&
        "response" in error &&
        error.response !== null &&
        typeof error.response === "object" &&
        "data" in error.response
          ? error.response.data
          : undefined;
      const message: string =
        typeof tsriError === "object" &&
        tsriError !== null &&
        "message" in tsriError
          ? String(tsriError.message)
          : Locale.getString(LocaleKeys.APP_STARTUP_ENDPOINTS_ERROR_MESSAGE);
      const serviceError = new TsriServiceEndpointError(
        tsriError !== undefined &&
          tsriError !== null &&
          typeof tsriError === "object" &&
          "code" in tsriError
          ? String(tsriError.code)
          : "",
        message
      );
      serviceError.setDescription(message);
      attachHttpContext(serviceError, error);
      Log.e(AppStartupInteractor.TAG, "fetchGlobalEndpoints()", serviceError);
      throw serviceError;
    }
  }

  /**
   * Resolves a service's basePath from the TSRI endpoint cache.
   *
   * Async because a cache miss is recoverable: `TsriEndpointResolver`
   * re-fetches the catalog on the INTERACTIVE policy, behind the alert overlay
   * for a critical service, before giving up. Previously this rethrew the
   * domain lib's bare "no entry" error on the spot.
   */
  public async getServiceEndpointByServiceName(
    serviceName: string
  ): Promise<string> {
    Log.d(AppStartupInteractor.TAG, "getServiceEndpointByServiceName() called");
    return TsriEndpointResolver.getInstance().resolve(serviceName);
  }

  /**
   * Builds TServicesParams for the branding service.
   * Uses the branding service endpoint from the TSRI endpoint cache.
   * No access token — this is a pre-sign-in call.
   */
  private async buildBrandingServicesParams(): Promise<TServicesParams> {
    const brandingBaseUrl: string = await this.getServiceEndpointByServiceName(
      TIVO_SERVICES.DeviceConfigService
    );
    const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
    return common.toServicesParams({
      basePath: transformBaseUrl(brandingBaseUrl)
    });
  }

  /**
   * Extracts a service error's code and message from a caught error.
   * A service error code (error.response.data.code) is preferred;
   * device-configuration-service's 400/404/405/500 bodies carry no such
   * code, so the HTTP status (error.response.status) is used as fallback.
   * Shared by {@link buildMsoDefaultDeviceConfigError} and
   * {@link buildMsoDefaultStringOverridesError} — same service, same
   * error-body shape.
   */
  private static extractServiceErrorDetails(
    error: unknown,
    defaultMessage: string
  ): { code: string; message: string } {
    const responseData: unknown =
      error !== null &&
      typeof error === "object" &&
      "response" in error &&
      error.response !== null &&
      typeof error.response === "object" &&
      "data" in error.response
        ? error.response.data
        : undefined;
    const statusCode: string | undefined =
      error !== null &&
      typeof error === "object" &&
      "response" in error &&
      error.response !== null &&
      typeof error.response === "object" &&
      "status" in error.response
        ? String(error.response.status)
        : undefined;
    const serverCode: string | undefined =
      responseData !== null &&
      typeof responseData === "object" &&
      "code" in responseData
        ? String(responseData.code)
        : undefined;
    const message: string =
      responseData !== null &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : defaultMessage;
    const fallbackCode: string = statusCode !== undefined ? statusCode : "";
    const code: string = serverCode !== undefined ? serverCode : fallbackCode;
    return { code, message };
  }

  /**
   * Builds a MsoDefaultDeviceConfigError from a caught msoDefaultDeviceConfig
   * error.
   */
  private static buildMsoDefaultDeviceConfigError(
    error: unknown
  ): MsoDefaultDeviceConfigError {
    const { code, message } = AppStartupInteractor.extractServiceErrorDetails(
      error,
      "MSO default device config fetch failed"
    );
    const serviceError = new MsoDefaultDeviceConfigError(code, message);
    serviceError.setDescription(message);
    return attachHttpContext(serviceError, error);
  }

  /**
   * Builds a MsoDefaultStringOverridesError from a caught
   * msoDefaultStringOverrides error.
   */
  private static buildMsoDefaultStringOverridesError(
    error: unknown
  ): MsoDefaultStringOverridesError {
    const { code, message } = AppStartupInteractor.extractServiceErrorDetails(
      error,
      "MSO default string overrides fetch failed"
    );
    const serviceError = new MsoDefaultStringOverridesError(code, message);
    serviceError.setDescription(message);
    return serviceError;
  }

  /**
   * Shared body for the msoName-only device-configuration-service pre-signin
   * fetches (msoDefaultDeviceConfig, msoDefaultStringOverrides): same
   * service (resolved via {@link buildBrandingServicesParams}), same
   * request shape, same DiagnosticLogger telemetry convention. Kept generic
   * over the request/response rather than folded into one fetch because the
   * two responses are consumed by different downstream layers: branding by
   * {@link Branding}, string overrides by the string-resolution layer.
   */
  private async fetchMsoDefaultResource<T, E>(
    apiName: string,
    logLabel: string,
    invokeService: (
      service: DeviceConfigurationService,
      msoName: string,
      signal?: AbortSignal
    ) => Promise<T>,
    buildError: (error: unknown) => E,
    signal?: AbortSignal
  ): Promise<T> {
    try {
      Log.d(AppStartupInteractor.TAG, `${logLabel}() called`);
      const startTime: number = Date.now();

      const tivoSettings = DeviceModel.getTiVoSettings();
      const msoName: string = tivoSettings.getMsoName();

      const service: DeviceConfigurationService =
        new DeviceConfigurationService(
          await this.buildBrandingServicesParams()
        );
      const result: T = await invokeService(service, msoName, signal);
      DiagnosticLogger.log(
        "pre_signin_api",
        `${apiName} succeeded`,
        DiagnosticSeverity.Info,
        {
          api: apiName,
          status: "success",
          latency_ms: String(Date.now() - startTime)
        }
      );
      return result;
    } catch (error: unknown) {
      DiagnosticLogger.log(
        "pre_signin_api",
        `${apiName} failed`,
        DiagnosticSeverity.Error,
        {
          api: apiName,
          status: "failure",
          error_type:
            error instanceof Error ? error.constructor.name : "unknown"
        }
      );
      const serviceError: E = buildError(error);
      Log.e(AppStartupInteractor.TAG, `${logLabel}()`, serviceError);
      throw serviceError;
    }
  }

  public async fetchMsoDefaultBrandingUiBundle(
    signal?: AbortSignal
  ): Promise<DeviceBrandingConfig> {
    return this.fetchMsoDefaultResource<
      DeviceBrandingConfig,
      MsoDefaultDeviceConfigError
    >(
      "msoDefaultDeviceConfig",
      "fetchMsoDefaultBrandingUiBundle",
      (service, msoName, sig) =>
        service.fetchMsoDefaultDeviceConfig({ msoName: msoName }, sig),
      AppStartupInteractor.buildMsoDefaultDeviceConfigError,
      signal
    );
  }

  /**
   * Fetches the MSO default (pre-sign-in) string overrides.
   */
  public async fetchMsoDefaultStringOverrides(
    signal?: AbortSignal
  ): Promise<StringOverrides> {
    return this.fetchMsoDefaultResource<
      StringOverrides,
      MsoDefaultStringOverridesError
    >(
      "msoDefaultStringOverrides",
      "fetchMsoDefaultStringOverrides",
      (service, msoName, sig) =>
        service.fetchMsoDefaultStringOverrides({ msoName: msoName }, sig),
      AppStartupInteractor.buildMsoDefaultStringOverridesError,
      signal
    );
  }

  public getPartnerBrandingImageUrl(width: number, height: number): string {
    try {
      const scaler: DimensionScaler = DimensionScaler.getInstance();
      const { width: scaledWidth, height: scaledHeight } =
        scaler.getScaledDimensions(width, height);
      return DeviceConfigurationService.getPartnerBrandingImageUrl(
        scaledWidth,
        scaledHeight
      );
    } catch (error: unknown) {
      Log.e(AppStartupInteractor.TAG, "getPartnerBrandingImageUrl()", error);
      return "";
    }
  }

  public async initFallbackBrandings(
    locale: string,
    msoName?: string
  ): Promise<void> {
    try {
      Log.d(AppStartupInteractor.TAG, "initFallbackBrandings() called");
      await Branding.setFallbackBrandings(locale, msoName);
      Log.d(AppStartupInteractor.TAG, "initFallbackBrandings() complete");
    } catch (error: unknown) {
      Log.e(AppStartupInteractor.TAG, "initFallbackBrandings()", error);
    }
  }
}
